import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Alert,
  Stack
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  AlertBannerContext,
  BRAND_COLOR,
  BRAND_HOVER,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  isGraphQLHandledAlertError
} from "@kivunova/kivufrontendcommon";
import { ROUTE_PLANS } from "../../constants/routes";
import {
  setSelectedPlanForCheckout,
  type SelectedPlanForCheckout
} from "../../store/slices/planSlice";
import AnimatedContainer from "../common/AnimatedContainer";
import { useCreateSubscription } from "../../hooks/api/useSubscription";
import { useIremboSubscriptionPaymentFlow } from "../../hooks/useIremboSubscriptionPaymentFlow";
import ContactCompanyAdminModal from "../Subscriptions/ContactCompanyAdminModal";
import AlreadyHasSubscriptionModal from "../Subscriptions/AlreadyHasSubscriptionModal";
import { toBannerCode } from "../../Utils/toBannerCode";
import type { RootState } from "../../store/store";

/** Matches subscription / plans marketing pages: centered column, readable line length */
const ORDER_SUMMARY_CARD_MAX_PX = 560;

const OrderSummary: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const { setAlert } = useContext(AlertBannerContext);
  const createSubscriptionMutation = useCreateSubscription();
  const { startCheckout } = useIremboSubscriptionPaymentFlow();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showContactAdminModal, setShowContactAdminModal] = useState(false);
  const [showAlreadyHasSubscriptionModal, setShowAlreadyHasSubscriptionModal] =
    useState(false);
  const selectedPlanFromRedux = useSelector(
    (state: RootState) => state.plans.selectedPlanForCheckout
  );
  const planData: SelectedPlanForCheckout | null =
    selectedPlanFromRedux ?? null;

  const showBannerError = (
    message: string,
    code: ALERT_BANNER_CODE = ALERT_BANNER_CODE.INTERNAL_ERROR
  ) => {
    setErrorMessage(message);
    setAlert({ severity: ALERT_BANNER_CODE_SEVERITY.ERROR, code });
  };

  const handleBack = () => {
    dispatch(setSelectedPlanForCheckout(null));
    navigate(localizedPath(ROUTE_PLANS, language, defaultLang));
  };

  const handleAgreeAndProceed = async () => {
    setErrorMessage("");
    if (!planData?.billingCycleId) {
      showBannerError(
        t(
          "order-summary-billing-cycle-missing",
          "Billing cycle is missing. Please go back and select a plan."
        ),
        ALERT_BANNER_CODE.VALIDATION_ERROR
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await createSubscriptionMutation.mutateAsync({
        planId: planData.planId,
        billingCycleId: planData.billingCycleId
      });
      const code = res.createSubscription?.code;
      if (code !== "OK") {
        if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
          setShowContactAdminModal(true);
          showBannerError(
            t(
              "order-summary-subscription-forbidden",
              "We could not start your subscription. You may not have permission—ask your company admin for help."
            ),
            toBannerCode(code)
          );
        } else if (code === "DUPLICATE_ERROR") {
          setShowAlreadyHasSubscriptionModal(true);
          showBannerError(
            t(
              "order-summary-subscription-duplicate",
              "This company already has an active subscription."
            ),
            ALERT_BANNER_CODE.DUPLICATE_ERROR
          );
        } else {
          showBannerError(
            t(
              "order-summary-subscription-failed",
              "We could not start your subscription. Please try again."
            ),
            toBannerCode(code)
          );
        }
        return;
      }

      const subscriptionId = res.createSubscription?.subscriptionId;
      if (!subscriptionId) {
        showBannerError(
          t(
            "order-summary-subscription-missing-id",
            "We could not start your subscription. Please try again or contact support."
          )
        );
        return;
      }

      await startCheckout(subscriptionId, {
        successDestination: "success",
        redirectOnIncomplete: true
      });
      dispatch(setSelectedPlanForCheckout(null));
    } catch (error: unknown) {
      if (!isGraphQLHandledAlertError(error)) {
        showBannerError(
          error instanceof Error && error.message.trim()
            ? error.message
            : t(
                "order-summary-network-or-unexpected-error",
                "Something went wrong. Check your connection and try again."
              ),
          ALERT_BANNER_CODE.SERVICE_UNAVAILABLE
        );
      } else {
        setErrorMessage(
          t(
            "order-summary-request-failed-see-banner",
            "The request could not be completed. See the notification at the top of the page."
          )
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!planData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <Paper
            sx={{
              width: "100%",
              maxWidth: ORDER_SUMMARY_CARD_MAX_PX,
              p: { xs: 3, sm: 4 },
              textAlign: "center",
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(8px)"
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.5 }}>
              {t("order-summary-no-plan-title", "No plan selected")}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 420, mx: "auto" }}
            >
              {t(
                "order-summary-no-plan-description",
                "Please select a plan to continue."
              )}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={handleBack}
                sx={{
                  backgroundColor: BRAND_COLOR,
                  "&:hover": { backgroundColor: BRAND_HOVER },
                  py: 1.25,
                  px: 4,
                  minWidth: { xs: "100%", sm: 220 },
                  maxWidth: 360,
                  borderRadius: 2,
                  fontWeight: "bold"
                }}
              >
                {t("order-summary-browse-plans", "Browse Plans")}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  /** Per-period rate for recurring copy; only when duration is known (avoids labeling total as "per month"). */
  const periodRateDisplay =
    planData.durationValue != null &&
    planData.durationValue > 0 &&
    planData.durationUnit != null
      ? {
          amount:
            Math.round((planData.price / planData.durationValue) * 100) / 100,
          unit: planData.durationUnit
        }
      : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        py: { xs: 5, sm: 6 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Container maxWidth="lg">
        <AnimatedContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 5 } }}>
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: {
                  xs: "1.35rem",
                  sm: "1.75rem",
                  md: "2.35rem"
                },
                lineHeight: 1.3,
                mb: 2,
                color: "white",
                letterSpacing: { xs: "0.01em", md: "0.02em" },
                textShadow: "0 2px 10px rgba(0,0,0,0.2)"
              }}
            >
              {t("order-summary-page-title", "Review subscription")}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem" },
                lineHeight: 1.7,
                maxWidth: 560,
                mx: "auto",
                color: "rgba(255,255,255,0.9)",
                fontWeight: 400,
                px: { xs: 0.5, sm: 0 }
              }}
            >
              {t(
                "order-summary-page-description",
                "Review your subscription amount and billing details. When you continue, we will create your subscription and open payment."
              )}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: ORDER_SUMMARY_CARD_MAX_PX,
              mx: "auto",
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              bgcolor: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 10px 40px rgba(0,0,0,0.12)"
            }}
          >
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{
                mb: 3,
                color: "text.secondary",
                textTransform: "none",
                fontWeight: 600,
                px: 0.5,
                "&:hover": { bgcolor: "action.hover" }
              }}
            >
              {t("order-summary-back", "Back to plans")}
            </Button>

            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1, letterSpacing: "-0.01em" }}
                >
                  {planData.name}
                </Typography>
                {planData.description && (
                  <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {planData.description}
                  </Typography>
                )}
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  borderColor: "divider"
                }}
              >
                <Stack spacing={1.25}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {t(
                      "order-summary-total-amount",
                      "Total subscription amount"
                    )}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    component="p"
                    sx={{ m: 0 }}
                  >
                    {planData.currency} {planData.price.toLocaleString()}
                  </Typography>
                  {periodRateDisplay ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("order-summary-then-billed", "Then billed")}{" "}
                      {planData.currency}{" "}
                      {periodRateDisplay.amount.toLocaleString()}{" "}
                      {periodRateDisplay.unit === "DAY"
                        ? t("order-summary-per-day", "per day")
                        : periodRateDisplay.unit === "MONTH"
                          ? t("order-summary-per-month", "per month")
                          : t("order-summary-per-year", "per year")}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    {t("order-summary-cycle-label", "Billing cycle")}:{" "}
                    {planData.billingCycle}
                  </Typography>
                </Stack>
              </Paper>

              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage("")}>
                  {errorMessage}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 1,
                  mt: 0.5,
                  borderTop: "1px solid",
                  borderColor: "divider"
                }}
              >
                <LoadingButton
                  variant="contained"
                  loading={submitting}
                  onClick={handleAgreeAndProceed}
                  sx={{
                    backgroundColor: BRAND_COLOR,
                    "&:hover": { backgroundColor: BRAND_HOVER },
                    py: 1.5,
                    px: { xs: 2, sm: 4 },
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 280 },
                    maxWidth: 400,
                    fontWeight: "bold",
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem"
                  }}
                >
                  {submitting
                    ? t("order-summary-processing", "Processing…")
                    : t(
                        "order-summary-create-and-pay",
                        "Create subscription and pay"
                      )}
                </LoadingButton>
              </Box>
            </Stack>
          </Paper>
        </AnimatedContainer>
      </Container>
      <ContactCompanyAdminModal
        open={showContactAdminModal}
        onClose={() => setShowContactAdminModal(false)}
      />
      <AlreadyHasSubscriptionModal
        open={showAlreadyHasSubscriptionModal}
        onClose={() => setShowAlreadyHasSubscriptionModal(false)}
      />
    </Box>
  );
};

export default OrderSummary;
